import { IconButton, SectionIcon } from "UIKit";
import { ContentSection } from "modules/contentRepository/types";
import { useCallback } from "react";
import { Variants, motion } from "framer-motion";
import { DeleteIcon, DragIcon, EditMonoIcon } from "Assets";

interface ContentSectionTileProps {
  section: ContentSection;
  isDragging?: boolean;
  isDraggable?: boolean;
  deleteDisabled?: boolean;
  onClick?: (section: ContentSection) => void;
  onEdit?: (section: ContentSection) => void;
  onDelete?: (section: ContentSection) => void;
  "data-testid"?: string;
}

const childrenVariants: Variants = {
  out: { x: -25, opacity: 0 },
  in: { x: 0, opacity: 1 },
};

const ContentSectionTile = ({
  section,
  isDragging = false,
  isDraggable = false,
  deleteDisabled,
  onClick,
  onEdit,
  onDelete,
  "data-testid": datTestId,
  ...props
}: ContentSectionTileProps) => {
  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      onClick && onClick(section);
    },
    [section, onClick],
  );

  const handleEdit = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      onEdit && onEdit(section);
    },
    [section, onEdit],
  );

  const handleDelete = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      onDelete && onDelete(section);
    },
    [section, onDelete],
  );

  return (
    <motion.div
      variants={childrenVariants}
      whileHover={{ scale: 1.01 }}
      className={`
        grid grid-cols-12 transition-all bg-white h-32
        p-5 rounded-lg shadow-sm
      ${onClick ? "cursor-pointer" : ""}
      ${isDragging ? "tile-border__dragging" : "tile-border"}
    `}
      onClick={handleClick}
      data-testid={datTestId}
      {...props}
    >
      <div className="row-start-1 col-start-1">
        {isDraggable && (
          <DragIcon
            data-testid="drag-icon"
            className="col-span-1 text-grey-500 self-start h-4 w-4"
          />
        )}
      </div>
      <div className="row-start-1 col-start-1 col-end-11 flex items-center ml-8">
        <SectionIcon iconClassname="w-10 h-10" iconURL={section.iconUrl || ""} />
        <p className="text-sm font-bold md:text-xl ml-5 flex-1 line-clamp-2">{section.name}</p>
      </div>
      <div className="row-start-1 row-end-2 col-start-12 flex flex-col justify-between">
        {onEdit && (
          <div className="self-end">
            <IconButton
              size="sm"
              onClick={handleEdit}
              icon={<EditMonoIcon />}
              data-testid={`edit-section-${section.name}`}
            />
          </div>
        )}
        {onDelete && (
          <div className="self-end">
            <IconButton
              disabled={deleteDisabled}
              size="sm"
              onClick={handleDelete}
              icon={<DeleteIcon />}
              data-tip
              data-for={`${section.id}_section_with_files`}
              data-testid={`delete-section-${section.name}`}
            />
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ContentSectionTile;
